/* eslint-disable react/prop-types */

import memoizeOne from 'memoize-one';
import React, { useRef, useState } from 'react';

import { accountsTypes } from 'app-components/flux/accounts';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DiamondIcon from '@mui/icons-material/Diamond';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HelpIcon from '@mui/icons-material/Help';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import PercentIcon from '@mui/icons-material/Percent';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { Field, getIn } from 'formik';
import { makeStyles } from 'tss-react/mui';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import NestedQMenu from 'components/NestedQMenu';

const useStyles = makeStyles()((theme) => ({
  root: {
    cursor: 'pointer',
    paddingRight: '7px',
    alignItems: 'center',
  },
  disabledIcons: {
    opacity: theme.palette.action.disabledOpacity,
  },
}));

const getIconForNode = (node) => {
  const { qcsType } = node;
  switch (qcsType) {
    case 'BANK':
      return AccountBalanceIcon;
    case 'CREDIT':
      return CreditCardIcon;
    case 'LOAN':
      return PercentIcon;
    case 'REAL_ESTATE':
      return MapsHomeWorkIcon;
    case 'VEHICLE':
      return DirectionsCarIcon;
    case 'OTHER_ASSET':
      return DiamondIcon;
    case 'INVESTMENT':
      return StackedLineChartIcon;
    default:
      return HelpIcon;
  }
};

// TODO: Move this back to accounts data in BIT
const isAccountNodeSelectable = (node, currentNode) => {
  const { qcsType } = node;
  const qcsTypeIsSupported =
    qcsType === 'BANK' ||
    qcsType === 'CASH' ||
    qcsType === 'CREDIT' ||
    qcsType === 'INVESTMENT' ||
    qcsType === 'LOAN' ||
    qcsType === 'OTHER_ASSET' ||
    qcsType === 'REAL_ESTATE' ||
    qcsType === 'VEHICLE' ||
    qcsType === 'UNKNOWN';

  // if qcsType is not supported, we are done
  if (!qcsTypeIsSupported) {
    return false;
  }

  // here we want to see if qcsType is supported for the currentNode type
  return (
    !currentNode ||
    currentNode.qcsType === 'UNKNOWN' ||
    (currentNode.qcsType === 'INVESTMENT' ? node.qcsType === 'INVESTMENT' : node.qcsType !== 'INVESTMENT')
  );
};

const getMenuOptionForNode = (node, depth, showManualTypes, currentNode, showManualInvestment) => {
  if (!isAccountNodeSelectable(node, currentNode)) {
    return null;
  }
  if (
    node.selectable &&
    (node.connectable || showManualTypes) &&
    (!showManualTypes || node.id !== 'INVESTMENT' || (showManualTypes && showManualInvestment)) // temporary until manual holdings released
  ) {
    if (node.children) {
      const childMenuItems = node.children.reduce((menuItems, child) => {
        const menuItem = getMenuOptionForNode(child, depth + 1, showManualTypes, currentNode, showManualInvestment);
        if (menuItem != null) {
          menuItems.push(menuItem);
        }
        return menuItems;
      }, []);

      const Icon = getIconForNode(node);
      return {
        ...(depth === 0 && { startIcon: <Icon /> }),
        label: node.displayName,
        value: node,
        onSelect: () => true,
        subMenu: childMenuItems,
      };
    }
    return { label: node.displayName, value: node };
  }
  return null;
};

function getMenuOptionsForNode(initialNode, showManualTypes, showManualInvestment) {
  return accountsTypes.AccountTypeNodesTree.reduce((menuItems, node) => {
    const menuItem = getMenuOptionForNode(node, 0, showManualTypes, initialNode, showManualInvestment);
    if (menuItem != null) {
      menuItems.push(menuItem);
    }
    return menuItems;
  }, []);
}
const memoizeGetMenuOptions = memoizeOne(getMenuOptionsForNode);

const MintAccountTypeSelector = (props) => {
  const {
    currentAccountType,
    name = 'accountType',
    size = 'medium',
    showManualTypes,
    sx,
    TextFieldProps = {},
    accId,
    ...other
  } = props;
  const { InputProps = {}, ...otherTextFieldProps } = TextFieldProps;

  const inputRef = useRef();
  const { classes } = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleChange = (form) => (val, _event) => {
    form.setFieldValue(name, val);
    setAnchorEl(null);
  };

  const showTip = inputRef.current && inputRef.current.offsetWidth < inputRef.current.scrollWidth;

  return (
    <Field name={name} {...other}>
      {({ field, form }) => {
        const Icon = field.value ? getIconForNode(field.value) : null;
        const accountTypeFormValue =
          field.value?.displayName === 'Unknown'
            ? ''
            : field.value?.displayName ||
              currentAccountType?.displayName ||
              form?.initialValues[name]?.displayName ||
              '';
        let typeVal;
        if (otherTextFieldProps.disabled && accountTypeFormValue) {
          typeVal = 'Type based on link';
        } else if (otherTextFieldProps.disabled) {
          typeVal = 'Account Type';
        } else {
          typeVal = accountTypeFormValue;
        }
        return (
          <Tooltip title={showTip ? accountTypeFormValue : ''}>
            <Box
              // So not remove this div - it is important for catching the tooltip ref, as field
              // is a 3rd party functional component and therefore cannot hold a ref
              sx={sx}
            >
              <TextField
                {...field}
                error={getIn(form.errors, accId)?.type && field.value === undefined}
                helperText={
                  getIn(form.errors, accId)?.type && field.value === undefined && getIn(form.errors, accId)?.type
                }
                label={'Account Type'}
                onChange={() => true}
                onClick={(e) => !otherTextFieldProps.disabled && setAnchorEl(e.currentTarget)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    setAnchorEl(e.currentTarget);
                  }
                }}
                size={size}
                placeholder='Account Type'
                value={typeVal}
                InputProps={{
                  classes: {
                    root: classes.root,
                    input: classes.root,
                  },
                  endAdornment: (
                    <InputAdornment position='end' sx={{ ...(anchorEl && { transform: 'rotate(180deg)' }) }}>
                      <ArrowDropDownRoundedIcon className={otherTextFieldProps.disabled ? classes.disabledIcons : ''} />
                    </InputAdornment>
                  ),
                  inputProps: {
                    ref: inputRef,
                    sx: { ...(size === 'small' && { typography: 'body2' }) },
                  },
                  readOnly: true,
                  ...(Icon && {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Icon fontSize={size} className={otherTextFieldProps.disabled ? classes.disabledIcons : ''} />
                      </InputAdornment>
                    ),
                  }),
                  variant: 'ellipsis',
                  ...InputProps,
                }}
                sx={sx}
                {...otherTextFieldProps}
              />
              <NestedQMenu
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                options={memoizeGetMenuOptions(field.value?.node, showManualTypes, true)}
                onChange={handleChange(form)}
                onClose={() => setAnchorEl(null)}
                component='span'
              />
            </Box>
          </Tooltip>
        );
      }}
    </Field>
  );
};

export default MintAccountTypeSelector;
